import { createSlice } from '@reduxjs/toolkit';
import {
  DETAILS_COMPANY,
  EMAIL_COMPANY,
  FILE_ID_LOGO_COMPANY,
  ID_FILE,
  INVOICE_ADDRESS_COMPANY,
  INVOICE_EMAIL_COMPANY,
  LOCATION_COMPANY,
  NAME_COMPANY,
  PUBLIC_URL_FILE,
  URL_COMPANY,
  URL_LOGO_COMPANY
} from '../../../common/adapters';
import {
  BASE_END_POINT_COMPANY,
  COMPANY_END_POINT,
  IS_VALID_DRAFT
} from '../../../config';
import {
  clearDraftCompanySession,
  getDraftCompanySession,
  saveDraftCompanySession
} from '../../../lib/sessionStorage';
import { getLocalStorageAuthentication } from '../../../lib/auth';
import { messageUpdated } from '../app/appSlice';
import { Severity } from '../../../components/Message/Message';
import { trackCompanyCreated } from '../../../helpers';

export const draftInitialCompany = {};
draftInitialCompany[NAME_COMPANY] = '';
draftInitialCompany[INVOICE_EMAIL_COMPANY] = '';
draftInitialCompany[INVOICE_ADDRESS_COMPANY] = '';
draftInitialCompany[FILE_ID_LOGO_COMPANY] = '';
draftInitialCompany[PUBLIC_URL_FILE] = '';
draftInitialCompany[EMAIL_COMPANY] = '';
draftInitialCompany[DETAILS_COMPANY] = null;
draftInitialCompany[URL_COMPANY] = '';
draftInitialCompany[IS_VALID_DRAFT] = false;
draftInitialCompany[LOCATION_COMPANY] = '';
draftInitialCompany[URL_LOGO_COMPANY] = '';

const initialState = {
  current: draftInitialCompany,
  draft: draftInitialCompany,
  loading: false,
  error: null,
  companyFetched: false,
  // Onboarding status
  onboarding: null,
  onboardingLoading: false,
  onboardingFetched: false,
  shouldRedirectAfterOnboarding: false
};

const companiesSlice = createSlice({
  name: 'companies',
  initialState,
  reducers: {
    getUserCompanySucceeded(state, action) {
      state.current = action.payload.company;
    },
    createCompanySucceeded(state, action) {
      state.draft = action.payload.company;
      state.current = action.payload.company;
    },
    draftCompanyUpdated(state, action) {
      saveDraftCompanySession(action.payload.company);
      state.draft = action.payload.company;
    },
    draftCompanySessionUpdated(state, action) {
      saveDraftCompanySession(action.payload.company);
    },
    draftLogoCompanyUpdated(state, action) {
      const companyDraft = { ...state.draft };
      companyDraft[FILE_ID_LOGO_COMPANY] = action.payload.file[ID_FILE];
      state.draft = companyDraft;
    },
    draftCompanyCacheCleared(state) {
      clearDraftCompanySession();
      state.draft = draftInitialCompany;
    },
    // Thunk reducers
    fetchCompanyRequest(state) {
      state.loading = true;
      state.error = null;
    },
    fetchCompanySuccess(state, action) {
      state.loading = false;
      state.current = action.payload.company;
      state.error = null;
      state.companyFetched = true;
    },
    fetchCompanyFailure(state, action) {
      state.loading = false;
      state.error = action.payload.error;
      state.companyFetched = true;
    },
    // Onboarding actions
    fetchOnboardingRequest(state) {
      state.onboardingLoading = true;
    },
    fetchOnboardingSuccess(state, action) {
      state.onboardingLoading = false;
      state.onboarding = action.payload;
      state.onboardingFetched = true;
    },
    fetchOnboardingFailure(state) {
      state.onboardingLoading = false;
      state.onboardingFetched = true;
    },
    updateOnboardingStatus(state, action) {
      state.onboarding = action.payload;
      // Set redirect flag when onboarding is completed
      if (action.payload?.status === 'COMPLETED') {
        state.shouldRedirectAfterOnboarding = true;
      }
    },
    clearOnboardingRedirectFlag(state) {
      state.shouldRedirectAfterOnboarding = false;
    }
  }
});

export const currentCompanySelector = (state) =>
  state.companies.current || draftInitialCompany;
export const currentCompanyNameSelector = (state) =>
  currentCompanySelector(state)?.[NAME_COMPANY] || '';
export const currentCompanySiteUrlSelector = (state) =>
  currentCompanySelector(state)?.[URL_COMPANY] || '';
export const draftCompanySelector = (state) =>
  getDraftCompanySession() ? getDraftCompanySession() : state.companies.draft;
export const loadedCompanySelector = (state) =>
  state.companies.draft.id
    ? state.companies.draft
    : currentCompanySelector(state);
export const isValidDraftCompanySelector = (state) =>
  state.companies.draft[IS_VALID_DRAFT];
export const companyFetchedSelector = (state) => state.companies.companyFetched;
export const companyOnboardingSelector = (state) => state.companies.onboarding;
export const companyOnboardingLoadingSelector = (state) =>
  state.companies.onboardingLoading;
export const companyOnboardingFetchedSelector = (state) =>
  state.companies.onboardingFetched;
export const companyShouldRedirectAfterOnboardingSelector = (state) =>
  state.companies.shouldRedirectAfterOnboarding;

export const {
  getUserCompanySucceeded,
  draftCompanyUpdated,
  draftLogoCompanyUpdated,
  draftCompanySessionUpdated,
  createCompanySucceeded,
  draftCompanyCacheCleared,
  fetchCompanyRequest,
  fetchCompanySuccess,
  fetchCompanyFailure,
  fetchOnboardingRequest,
  fetchOnboardingSuccess,
  fetchOnboardingFailure,
  updateOnboardingStatus,
  clearOnboardingRedirectFlag
} = companiesSlice.actions;

// Thunk to fetch company by user ID
export const fetchCompanyByUserId =
  ({ userId, isPublic = false }) =>
  async (dispatch, getState) => {
    const state = getState();
    const isLoading = state.companies.loading;
    const currentCompany = state.companies.current;

    // Skip if currently loading
    if (isLoading) {
      console.log('🔄 [Company Thunk] Already loading - skipping');
      return;
    }

    // Skip if already have company data (unless it's a public request)
    if (!isPublic && currentCompany && currentCompany.id) {
      console.log('🔄 [Company Thunk] Already have company data - skipping');
      return;
    }

    console.log('🔍 [Company Thunk] Fetching company for userId:', userId);

    dispatch(fetchCompanyRequest());

    try {
      const userIdEncoded = encodeURIComponent(userId);
      const url = `${BASE_END_POINT_COMPANY}${COMPANY_END_POINT}/user/${userIdEncoded}`;

      const headers = {
        'Content-Type': 'application/json'
      };

      // Only add authorization header if not a public request
      if (!isPublic) {
        const localAuth = getLocalStorageAuthentication();
        if (localAuth && localAuth.token) {
          headers.Authorization = `Bearer ${localAuth.token}`;
        }
      }

      console.log('🌐 [Company Thunk] Fetching from:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const company = Array.isArray(data?.data) ? data.data[0] : data?.data;

      console.log('✅ [Company Thunk] Company fetched successfully');

      dispatch(fetchCompanySuccess({ company }));

      // Only dispatch to Redux if NOT a public request
      if (!isPublic && company && company.id) {
        dispatch(getUserCompanySucceeded({ company }));
      }

      return company;
    } catch (error) {
      console.error('❌ [Company Thunk] Failed to fetch company:', error);
      dispatch(fetchCompanyFailure({ error: error.message }));
      throw error;
    }
  };

// Thunk to create company
export const createCompany =
  ({ company }) =>
  async (dispatch) => {
    console.log('🔍 [Company Thunk] Creating company');

    try {
      const localAuth = getLocalStorageAuthentication();
      company.userId = localAuth.userId;
      company.id = null;
      company.createdAt = null;
      company.updatedAt = null;

      const url = `${BASE_END_POINT_COMPANY}${COMPANY_END_POINT}`;

      console.log('🌐 [Company Thunk] Creating at:', url);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localAuth.token}`
        },
        body: JSON.stringify(company)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('📦 [Company Thunk] Raw response data:', data);

      // Handle both Company and potentially wrapped structures
      const createdCompany = data?.data?.company || data?.data;
      console.log('📦 [Company Thunk] Extracted company:', createdCompany);

      console.log('✅ [Company Thunk] Company created successfully');

      if (createdCompany && createdCompany.id) {
        saveDraftCompanySession(createdCompany);
        dispatch(createCompanySucceeded({ company: createdCompany }));

        // Track company creation in Google Analytics
        trackCompanyCreated(
          createdCompany.id,
          createdCompany[NAME_COMPANY] || 'Unknown',
          {
            has_logo: !!createdCompany[FILE_ID_LOGO_COMPANY],
            location: createdCompany[LOCATION_COMPANY] || 'Not specified',
            has_website: !!createdCompany[URL_COMPANY],
            has_email: !!createdCompany[EMAIL_COMPANY]
          }
        );

        // Show success message
        dispatch(
          messageUpdated({
            text: 'Company created successfully',
            severity: Severity.SUCCESS
          })
        );

        return createdCompany;
      } else {
        throw new Error('Invalid company data received');
      }
    } catch (error) {
      console.error('❌ [Company Thunk] Failed to create company:', error);

      // Show error message
      dispatch(
        messageUpdated({
          text: 'Company not created, please try again',
          severity: Severity.ERROR
        })
      );

      throw error;
    }
  };

// Thunk to update company
export const updateCompany =
  ({ company }) =>
  async (dispatch) => {
    console.log('🔍 [Company Thunk] Updating company:', company.id);

    try {
      const localAuth = getLocalStorageAuthentication();

      const url = `${BASE_END_POINT_COMPANY}${COMPANY_END_POINT}`;

      console.log('🌐 [Company Thunk] Updating at:', url);

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localAuth.token}`
        },
        body: JSON.stringify(company)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('📦 [Company Thunk] Raw response data:', data);

      // Handle both Company and potentially wrapped structures
      const updatedCompany = data?.data?.company || data?.data;
      console.log('📦 [Company Thunk] Extracted company:', updatedCompany);

      console.log('✅ [Company Thunk] Company updated successfully');

      if (updatedCompany && updatedCompany.id) {
        saveDraftCompanySession(updatedCompany);
        dispatch(getUserCompanySucceeded({ company: updatedCompany }));

        // Show success message
        dispatch(
          messageUpdated({
            text: 'Company updated successfully',
            severity: Severity.SUCCESS
          })
        );

        return updatedCompany;
      } else {
        throw new Error('Invalid company data received');
      }
    } catch (error) {
      console.error('❌ [Company Thunk] Failed to update company:', error);

      // Show error message
      dispatch(
        messageUpdated({
          text: 'Company not updated, please try again',
          severity: Severity.ERROR
        })
      );

      throw error;
    }
  };

// ========== ONBOARDING THUNKS ==========

// Thunk to fetch company onboarding status
export const fetchCompanyOnboarding = () => async (dispatch) => {
  console.log('📋 [Company Slice] Fetching company onboarding status');
  dispatch(fetchOnboardingRequest());

  try {
    const localAuth = getLocalStorageAuthentication();

    if (!localAuth || !localAuth.token) {
      throw new Error('Not authenticated');
    }

    const url = `${BASE_END_POINT_COMPANY}/api/v1/company/onboarding`;

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${localAuth.token}`
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to fetch company onboarding status: ${errorText}`
      );
    }

    const data = await response.json();
    const onboarding = data.data || null;

    dispatch(fetchOnboardingSuccess(onboarding));

    return { success: true, onboarding };
  } catch (error) {
    console.error('❌ [Company Slice] Error fetching onboarding:', error);
    dispatch(fetchOnboardingFailure());
    return { success: false, error: error.message };
  }
};

// Thunk to update company onboarding progress
export const updateCompanyOnboardingProgress =
  (currentStep) => async (dispatch) => {
    console.log('🔄 [Company Slice] Updating company onboarding progress');

    try {
      const localAuth = getLocalStorageAuthentication();

      if (!localAuth || !localAuth.token) {
        throw new Error('Not authenticated');
      }

      const url = `${BASE_END_POINT_COMPANY}/api/v1/company/onboarding/progress`;

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localAuth.token}`
        },
        body: JSON.stringify({ currentStep })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to update company onboarding progress: ${errorText}`
        );
      }

      const data = await response.json();
      const onboarding = data.data || null;

      dispatch(updateOnboardingStatus(onboarding));

      return { success: true, onboarding };
    } catch (error) {
      console.error(
        '❌ [Company Slice] Error updating onboarding progress:',
        error
      );
      return { success: false, error: error.message };
    }
  };

// Thunk to complete company onboarding
export const completeCompanyOnboarding = () => async (dispatch) => {
  console.log('✅ [Company Slice] Completing company onboarding');

  try {
    const localAuth = getLocalStorageAuthentication();

    if (!localAuth || !localAuth.token) {
      throw new Error('Not authenticated');
    }

    const url = `${BASE_END_POINT_COMPANY}/api/v1/company/onboarding/complete`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localAuth.token}`
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to complete company onboarding: ${errorText}`);
    }

    const data = await response.json();
    const onboarding = data.data || null;

    dispatch(updateOnboardingStatus(onboarding));

    dispatch(
      messageUpdated({
        text: 'Company profile completed successfully!',
        severity: Severity.SUCCESS
      })
    );

    return { success: true, onboarding };
  } catch (error) {
    console.error('❌ [Company Slice] Error completing onboarding:', error);

    dispatch(
      messageUpdated({
        text: error.message || 'Failed to complete onboarding',
        severity: Severity.ERROR
      })
    );

    return { success: false, error: error.message };
  }
};

// Thunk to skip company onboarding
export const skipCompanyOnboarding = () => async (dispatch) => {
  console.log('⏭️ [Company Slice] Skipping company onboarding');

  try {
    const localAuth = getLocalStorageAuthentication();

    if (!localAuth || !localAuth.token) {
      throw new Error('Not authenticated');
    }

    const url = `${BASE_END_POINT_COMPANY}/api/v1/company/onboarding/skip`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localAuth.token}`
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to skip company onboarding: ${errorText}`);
    }

    const data = await response.json();
    const onboarding = data.data || null;

    dispatch(updateOnboardingStatus(onboarding));

    return { success: true, onboarding };
  } catch (error) {
    console.error('❌ [Company Slice] Error skipping onboarding:', error);
    return { success: false, error: error.message };
  }
};

export default companiesSlice.reducer;
